import type { Theme } from "@mui/material";
import {
    Box,
    Card,
    CardContent,
    Grid,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Typography,
    Tooltip,
    useMediaQuery,
    Divider,
} from "@mui/material";
import { MouseEvent } from "react";
import { formatRelative } from "date-fns";
import {
    RecordContextProvider,
    SimpleListLoading,
    useListContext,
    useNotify,
} from "react-admin";
import { Link } from "react-router-dom";

// Material UI Icons
import LocalAtmIcon from "@mui/icons-material/LocalAtm"; // Cost Basis
import PriceChangeIcon from "@mui/icons-material/PriceChangeOutlined"; // Shares Held
import SwapHorizIcon from "@mui/icons-material/SwapHoriz"; // Unrealized P&L
import BusinessIcon from '@mui/icons-material/Business';
import CalendarTodayIcon from "@mui/icons-material/Today";
import TimelapseIcon from '@mui/icons-material/Timelapse';
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';

import CallMadeIcon from "@mui/icons-material/CallMade"; // Deposit
import CallReceivedIcon from "@mui/icons-material/CallReceived"; // Withdrawal
import RedeemIcon from "@mui/icons-material/Payments"; // Reward
import HourglassTopIcon from "@mui/icons-material/HourglassTop"; // Pending
import CheckCircleIcon from "@mui/icons-material/CheckCircle"; // Completed
import CancelIcon from "@mui/icons-material/Cancel"; // Failed
import TagIcon from "@mui/icons-material/Numbers";

import { Transaction } from "../../types/types";

function formatTimestamp(timestamp: number | string | Date): string {
    const date = new Date(timestamp);

    const day: string = String(date.getDate()).padStart(2, '0');
    const month: string = String(date.getMonth() + 1).padStart(2, '0');
    const year: string = String(date.getFullYear()).slice(-2);

    const hours: string = String(date.getHours()).padStart(2, '0');
    const minutes: string = String(date.getMinutes()).padStart(2, '0');
    const seconds: string = String(date.getSeconds()).padStart(2, '0');

    return `${day}.${month}.${year} - ${hours}:${minutes}:${seconds}`;
}

interface Props {
    txs: Transaction[];
}

export const TransactionListContent = ({ txs }: Props) => {
    const notify = useNotify();
    const isSmall = useMediaQuery((theme: Theme) => theme.breakpoints.down("md"));

    if (!txs.length) {
        return <SimpleListLoading hasLeftAvatarOrIcon hasSecondaryText />;
    }

    const now = new Date();

    // Function to get the correct icon for transfer_type
    const getTransferTypeIcon = (transferType: Transaction["transfer_type"]) => {
        switch (transferType) {
            case "deposit":
                return <CallMadeIcon color="primary" />;
            case "withdrawal":
                return <CallReceivedIcon color="primary" />;
            case "reward":
                return <RedeemIcon color="primary" />;
            default:
                return <></>;
        }
    };

    // Function to get the correct icon for transaction status
    const getStatusIcon = (status: Transaction["status"]) => {
        switch (status) {
            case "pending":
                return <HourglassTopIcon color="primary" />;
            case "canceled":
                return <CancelIcon color="error" />;
            case "completed":
                return <CheckCircleIcon color="success" />;
            default:
                return <></>;
        }
    };

    const handleCopy = (e: MouseEvent<HTMLSpanElement>, transactionId: string): void => {
        e.stopPropagation();
        e.preventDefault();
        navigator.clipboard.writeText(transactionId);
        notify("Transaction ID copied!");
    };

    return (
        <Card>
            <CardContent sx={{ paddingX: 0, paddingTop: 0, "&:last-child": { paddingBottom: 0 } }}>
                {/* Header Row */}
                <Grid container alignItems="center" spacing={0} sx={{ paddingX: 2, paddingY: 1, backgroundColor: "action.hover" }}>
                    <Grid item xs={0.5}>
                        <Typography variant="caption" sx={{ fontWeight: "bold", textTransform: "uppercase", display: "flex", alignItems: "center", gap: 0.5 }}>
                            <SwapHorizIcon fontSize="small" />
                        </Typography>
                    </Grid>
                    <Grid item xs={3}>
                        <Typography variant="caption" sx={{ fontWeight: "bold", textTransform: "uppercase", display: "flex", alignItems: "center", gap: 0.5 }}>
                            <BusinessIcon fontSize="small" /> Fund
                        </Typography>
                    </Grid>
                    <Grid item xs={1.5} sx={{ textAlign: "left" }}>
                        <Typography variant="caption" sx={{ fontWeight: "bold", textTransform: "uppercase", display: "flex", alignItems: "center", gap: 0.5 }}>
                            <PriceChangeIcon fontSize="small" /> Shares
                        </Typography>
                    </Grid>
                    <Grid item xs={1.5} sx={{ textAlign: "left" }}>
                        <Typography variant="caption" sx={{ fontWeight: "bold", textTransform: "uppercase", display: "flex", alignItems: "center", gap: 0.5 }}>
                            <LocalAtmIcon fontSize="small" /> Value
                        </Typography>
                    </Grid>
                    <Grid item xs={3} sx={{ textAlign: "left" }}>
                        <Typography variant="caption" sx={{ fontWeight: "bold", textTransform: "uppercase", display: "flex", alignItems: "center", gap: 0.5 }}>
                            From <ArrowRightAltIcon fontSize="small" /> To
                        </Typography>
                    </Grid>
                    <Grid item xs={2} sx={{ textAlign: "right" }}>
                        <Typography variant="caption" sx={{ fontWeight: "bold", textTransform: "uppercase", display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 0.5 }}>
                            <CalendarTodayIcon fontSize="small" />
                        </Typography>
                    </Grid>
                    <Grid item xs={0.5} sx={{ textAlign: "right" }}>
                        <Typography variant="caption" sx={{ fontWeight: "bold", textTransform: "uppercase", display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 0.5 }}>
                            <TimelapseIcon fontSize="small" />
                        </Typography>
                    </Grid>
                </Grid>

                <List dense sx={{ padding: 0 }}>
                    {txs.map((transaction, index) => (
                        <RecordContextProvider key={transaction.id} value={transaction}>
                            <ListItem disablePadding>
                                <ListItemButton
                                    component={Link}
                                    to={`/transactions/${transaction.id}/show`}
                                    sx={{
                                        flexDirection: "column",
                                        alignItems: "stretch",
                                        paddingY: 1,
                                    }}
                                >
                                    {/* Transaction ID (Left-Aligned) */}
                                    <Typography
                                        variant="body2"
                                        color="textSecondary"
                                        sx={{
                                            fontWeight: "bold",
                                            width: "100%",
                                            textAlign: "left",
                                            paddingBottom: 1,
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "4px",
                                        }}
                                    >
                                        <TagIcon sx={{ fontSize: "14px", color: "text.secondary", paddingRight: "2px" }} />

                                        <Box
                                            component="span"
                                            onClick={(e) => handleCopy(e, transaction.transaction_id)}
                                            sx={{
                                                cursor: "pointer",
                                                fontWeight: "bold",
                                                display: "inline",
                                                color: "inherit",
                                                transition: "color 0.2s ease-in-out",
                                                "&:hover": {
                                                    color: "primary.main", // Uses MUI theme color
                                                },
                                            }}
                                        >
                                            {transaction.transaction_id}
                                        </Box>
                                    </Typography>

                                    {/* Transaction Details Grid */}
                                    <Grid
                                        container
                                        alignItems="center"
                                        spacing={0}
                                        sx={{ width: "100%", padding: 0, margin: 0 }}
                                    >
                                        <Grid
                                            item
                                            xs={0.5}
                                            sx={{
                                                padding: 0,
                                                margin: 0,
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "flex-start",
                                            }}
                                        >
                                            <Tooltip title={transaction.transfer_type} arrow>
                                                {getTransferTypeIcon(transaction.transfer_type)}
                                            </Tooltip>
                                        </Grid>

                                        <Grid item xs={3}>
                                            <Typography variant="body2" color="textPrimary">
                                                {transaction.fund_name ?? "-"}
                                            </Typography>
                                        </Grid>

                                        <Grid item xs={1.5} sx={{ textAlign: "left" }}>
                                            <Typography
                                                variant="body2"
                                                color="textPrimary"
                                                sx={{ minWidth: 80 }}
                                            >
                                                {transaction.amount === 0 ? "-" : transaction.amount.toFixed(0)}
                                            </Typography>
                                            <Typography
                                                variant="caption"
                                                color="textSecondary"
                                                sx={{ fontWeight: "bold", fontSize: "0.75rem", opacity: 0.8 }}
                                            >
                                                {transaction.status === "pending" || transaction.status === "canceled"
                                                    ? "-"
                                                    : transaction.amount === 0
                                                        ? "-"
                                                        : (transaction.amount / 10).toFixed(2) + " €"}
                                            </Typography>
                                        </Grid>

                                        <Grid item xs={1.5} sx={{ textAlign: "left" }}>
                                            <Typography
                                                variant="body2"
                                                color="textPrimary"
                                                sx={{ minWidth: 80 }}
                                            >
                                                {(transaction.valuation?.['EUR'] ?? 0).toFixed(2)} €
                                            </Typography>
                                        </Grid>

                                        <Grid item xs={3} sx={{ display: "flex", flexDirection: "column", textAlign: "left", whiteSpace: "nowrap" }}>
                                            <Typography variant="caption" color="textSecondary" sx={{ opacity: 0.8 }}>
                                                {transaction.source_name ?? "-"}
                                            </Typography>
                                            <Typography variant="body2" color="textPrimary" >
                                                {transaction.target_name ?? "-"}
                                            </Typography>
                                        </Grid>

                                        <Grid item xs={2} textAlign="right">
                                            {transaction.timestamp && (
                                                <Typography
                                                    variant="body2"
                                                    color="textSecondary"
                                                    sx={{ fontSize: "0.85rem", opacity: 0.8 }}
                                                >
                                                    {formatTimestamp(transaction.timestamp)}
                                                </Typography>
                                            )}
                                        </Grid>

                                        <Grid
                                            item
                                            xs={0.5}
                                            sx={{
                                                padding: 0,
                                                margin: 0,
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "flex-end",
                                            }}
                                        >
                                            <Tooltip title={transaction.status} arrow>
                                                {getStatusIcon(transaction.status)}
                                            </Tooltip>
                                        </Grid>
                                    </Grid>
                                </ListItemButton>
                            </ListItem>
                            {index < txs.length - 1 && <Divider />}
                        </RecordContextProvider>
                    ))}
                    {txs.length === 0 && (
                        <ListItem>
                            <ListItemText primary="No transactions found" />
                        </ListItem>
                    )}
                </List>
            </CardContent>
        </Card >
    );
};